// SPDX-FileCopyrightText: © 2025 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  Watch,
} from '@stencil/core'
import type { LiteralUnion } from 'type-fest'

import type { QdsFocusEventDetail } from '../../helpers'
import { pickFocusEventAttributes } from '../../helpers'
import type { ButtonTarget } from '../button/button'
import type { Importance, Size } from '../shared'

export type IconButtonSize = Size | 'hero'
export type IconButtonImportance =
  | Importance
  | 'destructive'
  | 'subdued-destructive'

const isCounter = (value: unknown): value is number =>
  typeof value === 'string'
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === 'number'

const isIndicator = (value: unknown): value is '' | true =>
  value === '' || value === true

const preventDefault = (event: KeyboardEvent | MouseEvent): void => {
  event.preventDefault()
}
/**
 * The `<qds-icon-button>` is an interactive element activated by a user
 * with a mouse, keyboard, finger, voice command, or other assistive technology.
 * Once activated, it performs a programmable action, such as submitting a
 * [form](https://developer.mozilla.org/docs/Learn/Forms)
 * or opening a dialog.
 *
 * @see https://quartz.se.com/build/components/icon-button
 */
@Component({
  tag: 'qds-icon-button',
  formAssociated: true,
  styleUrl: 'icon-button.css',
  shadow: true,
})
export class IconButton implements ComponentInterface {
  /**
   * Prevents the icon button from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop()
  public readonly disabled: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName!: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The icon button's size.
   */
  @Prop() public readonly size?: Size | 'hero' = 'standard'

  /**
   * The icon button's importance.
   */
  @Prop() public readonly importance?: IconButtonImportance = 'standard'

  /**
   * Displays an indicator if `true` or the empty string. Otherwise, displays a
   * counter badge if a number or string representing a number is used.
   *
   * @webnative
   */
  @Prop() public readonly badge?: number | string | true

  /**
   * The indicator or counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string

  /**
   * The indicator's status.
   */
  @Prop()
  public readonly badgeIndicatorStatus?: HTMLQdsBadgeIndicatorElement['status'] =
    'neutral'

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<ButtonTarget, string>

  /**
   * When set, the underlying button will be rendered as an `<a>` with this
   * `href` instead of a `<button>`.
   *
   * @webnative
   */
  @Prop() public readonly href?: string

  /**
   * When href is define and the button act as a link (`<a>`), the `rel` will be added to the element.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string = 'noopener'

  /**
   * Tells the browser to download the linked file as this filename. Only used
   * when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly download?: string

  /**
   * The icon button's accessible name.
   */
  @Prop() public readonly text!: string

  /**
   * Emitted when the icon button loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the icon button gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  #savedTabIndex = 0

  #button?: HTMLAnchorElement | HTMLButtonElement

  #tooltip?: HTMLQdsTooltipElement

  get #computedSize(): IconButtonSize {
    switch (this.size) {
      case 'small':
      case 'large':
      case 'hero': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedImportance(): IconButtonImportance {
    switch (this.importance) {
      case 'emphasized':
      case 'subdued':
      case 'destructive':
      case 'subdued-destructive': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedDisabled(): boolean {
    return this.disabled
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.#computedDisabled.toString()
    if (this.#computedDisabled) this.host.removeAttribute('tabindex')
    else this.host.tabIndex = this.#savedTabIndex
  }

  @Watch('href')
  protected hrefChanged(): void {
    this.internals.role = this.href === undefined ? 'button' : 'link'
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string | null): void {
    if (newValue !== null) {
      const parsedValue = Number.parseInt(newValue, 10)
      this.#savedTabIndex =
        Number.isNaN(parsedValue) || !Number.isFinite(parsedValue)
          ? 0
          : parsedValue
    }
    if (this.#computedDisabled) this.host.removeAttribute('tabindex')
    else this.host.tabIndex = this.#savedTabIndex
  }

  @Watch('text')
  protected textChanged(): void {
    this.#updateAriaLabel()
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  @Listen('blur')
  protected async onBlur(event: FocusEvent): Promise<void> {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
    if (this.#tooltip) {
      await this.#tooltip.close()
    }
  }

  @Listen('focus')
  protected async onFocus(event: FocusEvent): Promise<void> {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
    if (this.#tooltip) {
      await this.#tooltip.show()
    }
  }

  @Listen('keydown')
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.host.click()
  }

  @Listen('keyup')
  protected onKeyup(event: KeyboardEvent): void {
    if (event.key === ' ') this.host.click()
  }

  public componentWillLoad(): void {
    this.hrefChanged()
    this.textChanged()
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
    this.disabledChanged()
  }

  public render() {
    const Tag = this.href === undefined ? 'button' : 'a'
    return (
      <div>
        <Tag
          type={Tag === 'a' ? undefined : 'button'}
          class={{
            'qds-icon-button': true,
            'qds-disabled': this.#computedDisabled,
          }}
          href={Tag === 'a' ? this.href : undefined}
          rel={Tag === 'a' ? this.rel : undefined}
          ref={this.#ref}
          download={Tag === 'a' ? this.download : undefined}
          aria-hidden="true"
          data-size={this.#computedSize}
          data-importance={this.#computedImportance}
          onKeyDown={preventDefault}
          onKeyUp={preventDefault}
          onKeyPress={preventDefault}
          tabIndex={-1}
        >
          <qds-icon
            class="qds-icon"
            data-size={this.#computedSize}
            library={this.iconLibrary}
            name={this.iconName}
          />
          {isIndicator(this.badge) && (
            <qds-badge-indicator
              class="qds-indicator"
              status={this.badgeIndicatorStatus}
              description={this.badgeDescription}
              size={this.#computedSize}
              strokeRing
            />
          )}
          {isCounter(this.badge) && (
            <qds-badge-counter
              class="qds-indicator"
              description={this.badgeDescription}
              importance={this.#computedImportance}
              size={this.#computedSize}
              strokeRing
              value={this.badge}
            />
          )}
        </Tag>
        {this.text && (
          <qds-tooltip ref={this.#tooltipRef}>{this.text}</qds-tooltip>
        )}
      </div>
    )
  }

  #updateAriaLabel(): void {
    this.internals.ariaLabel =
      this.badge !== undefined && this.badgeDescription !== undefined
        ? `${this.text} ${this.badge === true ? '' : this.badge} ${this.badgeDescription}`
        : this.text
  }

  readonly #ref = (button?: HTMLAnchorElement | HTMLButtonElement): void => {
    this.#button = button
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip) {
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#button
      this.#tooltip = tooltip
    }
  }
}
